/**
 * The remaining 8 NECS department command centers, built on the shared
 * primitives (`DeptTaskShared`) so each one is a lean, focused cockpit
 * with department-specific hero, KPI tiles, key responsibilities, and
 * task inbox. Extending is a 20-line addition — see any of the exports
 * below.
 *
 * Roles & responsibilities per department are taken directly from
 * `AdminDepartments.NECS_DEPARTMENTS` so the descriptions stay in sync
 * across the app.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DollarSign, Scale, TrendingUp, Megaphone, Headphones, Truck,
  ShieldCheck, GraduationCap, ArrowRightLeft, Receipt, FileCheck,
  Target, Camera, Star, Package, Siren, BookOpen, AlertTriangle,
  LayoutDashboard, BarChart3, Users, ClipboardList, QrCode, Trophy,
  FileText, MapPin, Activity, MessageCircle, Zap, ExternalLink,
  Sparkles, Leaf,
} from 'lucide-react';
import { KpiTile, DeptInbox, RouteTaskDialog } from './DeptTaskShared';
import DeptQuickAccess from './DeptQuickAccess';
import UnifiedGeoRoleFilter from '../UnifiedGeoRoleFilter';
import { LeaderboardCard } from './LeaderboardCard';
import CsDemoSharePanel from './CsDemoSharePanel';
import ServicePackagesPanel from './ServicePackagesPanel';
import QuotePipelineBoard from './QuotePipelineBoard';
import DraftContractsPanel from './DraftContractsPanel';
import CommunityCodesPanel from './CommunityCodesPanel';
import ResidentOpsPanel from './ResidentOpsPanel';
import CommercialEnginePanel from './CommercialEnginePanel';
import { DEPT_BY_KEY } from './AdminDepartments';
import { DepartmentDetails } from './DepartmentDetails';
import { useAdminScopeTerms } from '../../lib/adminScopeTerms';
import { AdminScopeSection } from './AdminScopeSection';
import { useDepartmentMenu } from './DepartmentNavigation';
import ResidentApprovalsQueue from '../ResidentApprovalsQueue';
import ChurchCareLaunchChecklist from './ChurchCareLaunchChecklist';
import AdminSupportChat from '../AdminSupportChat';
import ProcurementVehiclesPanel from '../procurement/ProcurementVehiclesPanel';
import ProcurementGasCardsPanel from '../procurement/ProcurementGasCardsPanel';
import FinanceFuelExpensePanel from '../procurement/FinanceFuelExpensePanel';
import VehicleMpgChart from '../procurement/VehicleMpgChart';
import AutoQuoteExistingClientPanel from './AutoQuoteExistingClientPanel';
import FloorLineItemPricingPanel from './FloorLineItemPricingPanel';
import CSContractEditorPanel from './CSContractEditorPanel';
import CSOnboardingListPanel from './CSOnboardingListPanel';
import CSTicketsBoardPanel from './CSTicketsBoardPanel';
import ExplorePlatformTile from './ExplorePlatformTile';
import TrashcanFulfillmentBoard from '../TrashcanFulfillmentBoard';
// Iter-156 · Phases 1/2/3/4/6a — Finance / Sales / CS tab-and-board
// primitive + department boards for Invoices, Contracts & Pricing,
// Renewal Alerts, Finance analytics, Executive scorecard, and the
// canonical Commercial Quote Engine.
import DeptTabBoard from './DeptTabBoard';
// Iter-182 · Phase 8 — Feb 2026 update. The full AdminRoutesRegions
// component (map + city clusters + region seed + bulk geocode) is now
// wired into every department via DeptRoutesRegionsCollapsible in the
// shared shell. This file no longer needs a direct import.
import InvoicesBoard from './InvoicesBoard';
import ContractsPricingBoard from './ContractsPricingBoard';
import FinanceAnalyticsBoard from './FinanceAnalyticsBoard';
import ExecutiveScorecardBoard from './ExecutiveScorecardBoard';
import RenewalAlertsBoard from './RenewalAlertsBoard';
import QuoteEngineBoard from './QuoteEngineBoard';
import SustainabilityConfigPanel from './SustainabilityConfigPanel';
// Iter-157 · Phase 3 — four dedicated per-property boards mounted as
// separate tabs on Finance, HR, Legal, Sales, CS command centers.
import {
  BillingARBoard,
  PropertiesPricingBoard,
  PropertiesContractsBoard,
  PropertiesServicesAgreementBoard,
} from './PropertiesBoards';
// Iter-160 · Phase 4 — Finance-specific Billing & A/R view with live
// notifications panel + unread-badge hook. Separate from BillingARBoard
// so the other departments keep their plain board and only Finance
// gets the notifications intake.
import FinanceBillingARWithNotifications, { useFinanceUnreadCount } from './FinanceBillingARWithNotifications';
// Iter-84 — each department landing hydrates the executive scorecard
// relevant to its function, so directors get the same real-time board
// the CEO sees on the home page. All wrapped in a mini GeoFilterProvider.
import {
  GeoFilterProvider, GeoFilterBar,
  SalesLeaderboardPanel, FinanceScorecardPanel,
  SafetyRiskPanel, HrLeadershipPanel,
} from './CeoStrategyPanels';

const API = process.env.REACT_APP_BACKEND_URL;

// Iter-157 · Phase 3 — the four distinct property-focused tabs mounted
// on every command center below. Each tab renders its own dedicated
// information board. NEVER combine or nest these under each other.
const PHASE3_PROPERTY_TABS = [
  { key: 'billing-ar',            label: 'Billing & A/R',              icon: Receipt,   render: () => <BillingARBoard /> },
  { key: 'properties-pricing',    label: 'Properties Pricing',         icon: DollarSign, render: () => <PropertiesPricingBoard /> },
  { key: 'properties-contracts',  label: 'Properties Contracts',       icon: FileCheck, render: () => <PropertiesContractsBoard /> },
  { key: 'properties-services',   label: 'Properties & Services Agreement', icon: ClipboardList, render: () => <PropertiesServicesAgreementBoard /> },
];

/**
 * Generic department shell — passes props to build any command center
 * with a consistent look (hero + responsibilities + KPI row + inbox).
 * Kept internal so every wrapper stays 20 lines.
 */
function DeptCommandShell({
  deptKey, accent, icon: Icon, title, subtitle,
  kpis = [], responsibilities = null, onOpenSection = null,
  boardFilter = null,
  children = null,
}) {
  const [route, setRoute] = useState(false);
  const dept = DEPT_BY_KEY[deptKey];
  const terms = useAdminScopeTerms();
  const responsibilityList = responsibilities || dept?.responsibilities || [];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid={`${deptKey}-command-center`}>
      {/* Hero */}
      <div
        className="rounded-2xl p-4 sm:p-6 border-2 flex flex-wrap items-start gap-4"
        style={{
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 60%, #2D2D2D 100%)',
          borderColor: `${accent}80`,
        }}
        data-testid={`${deptKey}-hero`}
      >
        <div className="h-16 w-16 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: `${accent}22`, border: `2px solid ${accent}` }}>
          <Icon className="h-8 w-8" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0 basis-48">
          <p className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: accent }}>
            {dept?.label} · Command Center · {dept?.code}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-1"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.02em' }} data-testid={`${deptKey}-command-title`}>
            {title}
          </h1>
          <p className="text-xs text-zinc-400 mt-3 max-w-3xl leading-relaxed">
            {terms.text(subtitle || dept?.mission)}
          </p>
        </div>
        <Button onClick={() => setRoute(true)} className="text-black font-bold"
                style={{ backgroundColor: accent }}
                data-testid={`${deptKey}-route-btn`}>
          <ArrowRightLeft className="h-4 w-4 mr-2" /> Route Work
        </Button>
      </div>

      {/* Iter-112 · Item 1 — canonical department layout:
          §1 presentation + KPIs + key responsibilities
          §2 Enterprise Pivot Filter (Corporate Admin | Valet | Cleaners)
          §3 company dedicated tabs (Quick Access + Inbox + dept sections)
          §4 high-efficiency leading boards at the very bottom.
          The standalone corporate/admin slicer (MetroKindHeader) was
          retired — Corporate Admin now lives inside the filter's Role row. */}

      <DepartmentDetails dept={dept} prefix={deptKey} responsibilities={responsibilityList} accent={accent} />
      {boardFilter && <UnifiedGeoRoleFilter filter={boardFilter} testIdPrefix={`unified-filter-${deptKey}`} />}

      {/* Department-specific metrics follow the shared population metrics. */}
      {kpis.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-widest mb-3 font-semibold" style={{ color: accent }} data-testid={`${deptKey}-department-metrics-heading`}>
            Department Metrics
          </h2>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map((k, i) => (
              <KpiTile key={i} {...k} tone={k.tone || accent} testid={`${deptKey}-kpi-${k.testkey || i}`} />
            ))}
          </div>
        </section>
      )}

      {/* §3 — Company dedicated tabs: Quick Access strip + Inbox. */}
      {onOpenSection && (
        <DeptQuickAccess deptKey={deptKey} accent={accent} onOpen={onOpenSection} />
      )}
      <DeptInbox deptKey={deptKey} title={`${dept?.label?.toUpperCase()} INBOX`} />

      {/* §3 — Additional department-specific sections. */}
      <div key={`${boardFilter?.queryString}-${boardFilter?.refreshTick}`} className="space-y-6 min-w-0">{children}</div>

      {/* Master Addendum · Routes & Regions collapsible — same click-to-
          populate widget every department gets. Legal, Customer Services,
          Sales & Business Development, and IT have full edit access;
          every other department sees a read-only view. */}
      <DeptRoutesRegionsCollapsible deptLabel={dept?.label || deptKey} />

      {/* §4 — High-efficiency leading board, always at the very bottom. */}
      <LeaderboardCard department={deptKey} data-testid={`${deptKey}-leading-board`} />

      <RouteTaskDialog open={route} onClose={() => setRoute(false)} defaultDept={deptKey} onSaved={() => setRoute(false)} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Finance & Accounting                                                    */
/* ═══════════════════════════════════════════════════════════════════════ */
export function FinanceCommandCenter({ onOpenSection, boardFilter }) {
  const [snapshot, setSnapshot] = useState({ contracts: 0, brokers: 0, invoicesMonth: 0, invoicesTotal: 0 });
  const { count: unreadCount, refresh: refreshUnread } = useFinanceUnreadCount();
  useEffect(() => {
    (async () => {
      try {
        const qs = boardFilter?.queryString ? `?${boardFilter.queryString}` : '';
        const [c, b, inv] = await Promise.all([
          fetch(`${API}/api/contracts${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/api/brokers${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/api/billing/invoices${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
        ]);
        const invList = Array.isArray(inv) ? inv : (inv?.invoices || []);
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        const invoicesMonth = invList.filter((i) => new Date(i.created_at || i.invoice_date || 0).getTime() >= monthStart).length;
        setSnapshot({ contracts: c.length || 0, brokers: b.length || 0, invoicesMonth, invoicesTotal: invList.length });
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.queryString, boardFilter?.refreshTick]);
  return (
    <DeptCommandShell
      boardFilter={boardFilter}
      deptKey="finance" accent="#f59e0b" icon={DollarSign}
      title="FINANCE HEADQUARTERS"
      onOpenSection={onOpenSection}
      kpis={[
        { icon: <FileCheck className="h-5 w-5" />, label: 'Active contracts', value: snapshot.contracts, sub: 'Billable properties', testkey: 'contracts' },
        { icon: <Truck className="h-5 w-5" />, label: 'Broker suppliers', value: snapshot.brokers, sub: 'On the AP ledger', testkey: 'brokers' },
        { icon: <Receipt className="h-5 w-5" />, label: 'Invoices this month', value: snapshot.invoicesMonth, sub: `${snapshot.invoicesTotal} total on file`, testkey: 'invoices' },
        { icon: <DollarSign className="h-5 w-5" />, label: 'Payroll headcount', value: '—', sub: 'Wire from HR headcount', testkey: 'payroll' },
      ]}
    >
      <div className="flex flex-wrap gap-4" data-testid="finance-workspace-links"><a href="/workforce-payroll" className="text-[#C9A962] underline" data-testid="finance-payroll-workspace">Rolling payroll & authorized exports</a><a href="/rewards-review" className="text-[#C9A962] underline" data-testid="finance-six-month-rewards-link">Six-month payroll reward review</a><a href="/finance-payables" className="text-[#C9A962] underline" data-testid="finance-supplier-payables-link">Supplier payable review · MOCKED QuickBooks</a></div>
      <GeoFilterProvider>
        <div className="mt-6 space-y-6" data-testid="finance-dept-scorecard">
          <GeoFilterBar />
          <FinanceScorecardPanel />
        </div>
      </GeoFilterProvider>
      {/* Iter-156 · Phases 1/2/3/4/6a — Finance tab-and-board pattern:
          Analytics · Invoices · Contracts · Renewals · Quote Engine ·
          Executive. Every future finance feature must mount here as a
          tab (Phase 4 UI standard). */}
      <div className="mt-6" data-testid="finance-iter156-boards">
        <DeptTabBoard
          testidPrefix="finance-tab-board"
          accent="#f59e0b"
          tabs={[
            // Iter-163 · Phase 5 — Finance-only Billing & A/R now
            // uses the unified panel inside the board itself; the
            // standalone tab badge has been removed to consolidate
            // notifications inside Billing & A/R.
            {
              key: 'billing-ar',
              label: 'Billing & A/R',
              icon: Receipt,
              render: () => <FinanceBillingARWithNotifications onCountChange={refreshUnread} />,
            },
            ...PHASE3_PROPERTY_TABS.filter((t) => t.key !== 'billing-ar'),
            { key: 'analytics', label: 'Finance Analytics', icon: BarChart3, render: () => <FinanceAnalyticsBoard /> },
            { key: 'invoices',  label: 'Invoices',          icon: Receipt,   render: () => <InvoicesBoard /> },
            { key: 'renewals',  label: 'Renewal Alerts',    icon: Package,   render: () => <RenewalAlertsBoard /> },
            { key: 'quote-engine', label: 'Quote Engine',   icon: Sparkles,  render: () => <QuoteEngineBoard /> },
            { key: 'executive', label: 'Executive View',    icon: TrendingUp, render: () => <ExecutiveScorecardBoard /> },
            { key: 'sustainability', label: 'Sustainability Config', icon: Leaf, render: () => <SustainabilityConfigPanel /> },
          ]}
        />
      </div>
      <FinanceFuelExpensePanel />
      <VehicleMpgChart testId="finance-mpg-chart" />
    </DeptCommandShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Legal & Compliance                                                       */
/* ═══════════════════════════════════════════════════════════════════════ */
export function LegalCommandCenter({ onOpenSection, boardFilter }) {
  const [contracts, setContracts] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const qs = boardFilter?.queryString ? `?${boardFilter.queryString}` : '';
        const r = await fetch(`${API}/api/contracts${qs}`, { credentials: 'include' });
        if (r.ok) { const d = await r.json(); setContracts(d.length || 0); }
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.queryString, boardFilter?.refreshTick]);
  return (
    <DeptCommandShell
      boardFilter={boardFilter}
      deptKey="legal" accent="#8b5cf6" icon={Scale}
      title="LEGAL HEADQUARTERS"
      onOpenSection={onOpenSection}
      kpis={[
        { icon: <FileCheck className="h-5 w-5" />, label: 'MSAs on file', value: contracts, sub: 'Master service agreements', testkey: 'msas' },
        { icon: <Scale className="h-5 w-5" />, label: 'Open disputes', value: '—', sub: 'Litigation wiring pending', testkey: 'disputes' },
        { icon: <ShieldCheck className="h-5 w-5" />, label: 'Compliance items', value: '—', sub: 'OSHA · EPA · Municipal', testkey: 'compliance' },
        { icon: <Package className="h-5 w-5" />, label: 'Renewals · 30 days', value: '—', sub: 'Auto-renewal windows', testkey: 'renewals' },
      ]}
    >
      {/* Iter-157 · Phase 3 — Legal tab-and-board with the four
          distinct per-property tabs. */}
      <DeptTabBoard
        testidPrefix="legal-tab-board"
        accent="#8b5cf6"
        tabs={PHASE3_PROPERTY_TABS}
      />
      {/* Routes & Regions is now provided by the shared DeptCommandShell
          collapsible above (auto-expanded for full-access depts). */}
    </DeptCommandShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Sales & Business Development                                             */
/* ═══════════════════════════════════════════════════════════════════════ */
export function SalesCommandCenter({ onOpenSection, boardFilter }) {
  const [pipeline, setPipeline] = useState({ inquiries: 0, contracts: 0 });
  useEffect(() => {
    (async () => {
      try {
        // Section 2 (iter-138) — append pivot filter queryString so
        // Sales inquiries + contracts scope to the selected board
        // dimensions (state / city / country / date-range / view_as).
        const qs = boardFilter?.queryString ? `?${boardFilter.queryString}` : '';
        const [inq, c] = await Promise.all([
          fetch(`${API}/api/inquiries${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/api/contracts${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
        ]);
        setPipeline({ inquiries: inq.length || 0, contracts: c.length || 0 });
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.queryString, boardFilter?.refreshTick]);
  return (
    <DeptCommandShell
      boardFilter={boardFilter}
      deptKey="sales" accent="#10b981" icon={TrendingUp}
      title="SALES HEADQUARTERS"
      onOpenSection={onOpenSection}
      kpis={[
        { icon: <Target className="h-5 w-5" />, label: 'Open inquiries', value: pipeline.inquiries, sub: 'From landing page', testkey: 'inquiries' },
        { icon: <FileCheck className="h-5 w-5" />, label: 'Signed contracts', value: pipeline.contracts, sub: 'YTD closed-won', testkey: 'closed' },
        { icon: <TrendingUp className="h-5 w-5" />, label: 'Renewal rate', value: '—', sub: 'YoY', testkey: 'renewal' },
        { icon: <DollarSign className="h-5 w-5" />, label: 'Pipeline value', value: '—', sub: 'Weighted', testkey: 'pipeline' },
      ]}
    >
      <GeoFilterProvider>
        <div className="mt-6 space-y-6" data-testid="sales-dept-scorecard">
          <GeoFilterBar />
          <SalesLeaderboardPanel />
        </div>
      </GeoFilterProvider>
      {/* D4/E11 — Service Packages + New Quote Submissions (synchronized with CS). */}
      <div className="mt-6 space-y-6">
        {/* Section 5 (iter-130) · Auto-quote for existing clients. */}
        <AutoQuoteExistingClientPanel testId="sales-auto-quote-panel" />
        {/* Section 3 (iter-133) · Floor services line-item pricing. */}
        <FloorLineItemPricingPanel testId="sales-floor-line-item-panel" />
        {/* S30 — Lead Status Pipeline over all quote leads. */}
        <QuotePipelineBoard />
        {/* S31 — Won deal handoff: draft contracts for Account Management. */}
        <DraftContractsPanel />
        <AdminScopeSection exclude="cleaners"><ServicePackagesPanel dept="sales" /></AdminScopeSection>
        <AdminScopeSection exclude="valet"><CommercialEnginePanel /></AdminScopeSection>
        {/* Iter-156 · Phase 3 — Sales tab-and-board with Quote Engine
            + Contracts + Renewal Alerts. */}
        <DeptTabBoard
          testidPrefix="sales-tab-board"
          accent="#10b981"
          tabs={[
            ...PHASE3_PROPERTY_TABS,
            { key: 'quote-engine', label: 'Quote Engine',    icon: Sparkles,  render: () => <QuoteEngineBoard /> },
            { key: 'renewals',     label: 'Renewal Alerts',  icon: Package,   render: () => <RenewalAlertsBoard /> },
          ]}
        />
        {/* Iter-150 · Red Team Fix 3 — public demo entry for Sales reps. */}
        <ExplorePlatformTile testId="sales-explore-platform" />
        {/* Routes & Regions is provided by DeptCommandShell (auto-expanded
            for Sales as a full-access department). */}
      </div>
    </DeptCommandShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Marketing & Communications                                               */
/* ═══════════════════════════════════════════════════════════════════════ */
export function MarketingCommandCenter({ onOpenSection, boardFilter }) {
  const [mkt, setMkt] = useState({ inquiries: 0, propertiesShowcased: 0 });
  useEffect(() => {
    (async () => {
      try {
        const qs = boardFilter?.queryString ? `?${boardFilter.queryString}` : '';
        const [inqRes, propRes] = await Promise.all([
          fetch(`${API}/api/inquiries${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/api/properties${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
        ]);
        setMkt({
          inquiries: (Array.isArray(inqRes) ? inqRes : []).length,
          propertiesShowcased: (Array.isArray(propRes) ? propRes : []).length,
        });
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.queryString, boardFilter?.refreshTick]);
  return (
    <DeptCommandShell
      boardFilter={boardFilter}
      deptKey="marketing" accent="#ec4899" icon={Megaphone}
      title="MARKETING HEADQUARTERS"
      onOpenSection={onOpenSection}
      kpis={[
        { icon: <Megaphone className="h-5 w-5" />, label: 'Website inquiries', value: mkt.inquiries, sub: 'Warm lead pipeline', testkey: 'inquiries' },
        { icon: <Camera className="h-5 w-5" />, label: 'Properties showcased', value: mkt.propertiesShowcased, sub: 'Portfolio breadth', testkey: 'assets' },
        { icon: <Star className="h-5 w-5" />, label: 'Active campaigns', value: '—', sub: 'Wire from CRM', testkey: 'campaigns' },
        { icon: <TrendingUp className="h-5 w-5" />, label: 'PR mentions', value: '—', sub: 'Rolling 30 days', testkey: 'pr' },
      ]}
    >
      {/* Iter-102 · Item 7 — ChurchCare Launch Checklist. Marketing
          Director confirms every step manually before campaigns go live. */}
      <AdminScopeSection exclude="valet"><ChurchCareLaunchChecklist /></AdminScopeSection>
    </DeptCommandShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Customer Service                                                         */
/* ═══════════════════════════════════════════════════════════════════════ */
import OpsStaffingRoster from '../OpsStaffingRoster';
import AdminSpecialtyServices from '../AdminSpecialtyServices';
import LeadsInboundCRMTab from './LeadsInboundCRMTab';
import DeptRoutesRegionsCollapsible from './DeptRoutesRegionsCollapsible';
import AdminViolationReportsTab from './AdminViolationReportsTab';
import ConvertibleDemosCard from './ConvertibleDemosCard';

// ── Operations-department tab catalog — replicated inside CS HQ so
// customer service reps triage from the exact same cockpit their
// Operations peers use (per CEO Feb 2026 directive). Tabs marked
// `inline: true` render their content directly in CS HQ; the rest
// deep-link to the Operations dashboard via `onOpenSection` (which
// already remembers `parentDept='customer-service'` for the back
// breadcrumb).
const CS_OPS_TABS = [
  { key: 'home', label: 'Home', icon: LayoutDashboard, inline: false },
  { key: 'statistics', label: 'Statistics', icon: BarChart3, inline: false },
  { key: 'resident-activations', label: 'Resident Activations', icon: ShieldCheck, inline: true },
  { key: 'support-chat', label: 'Live Chats', icon: MessageCircle, inline: true },
  { key: 'rewards', label: 'Rewards', icon: Trophy, inline: true },
  { key: 'violations', label: 'Violations', icon: AlertTriangle, inline: true },
  { key: 'ops-staffing', label: 'Staffing Agents', icon: Users, inline: true },
  { key: 'ops-training-completions', label: 'Training Completions', icon: GraduationCap, inline: false },
  { key: 'swaps', label: 'Swaps', icon: ArrowRightLeft, inline: false },
  { key: 'fleet-safety', label: 'Fleet Safety', icon: Truck, inline: false },
  { key: 'daily-reports', label: 'Daily Reports', icon: ClipboardList, inline: false },
  { key: 'qr-codes', label: 'QR Codes', icon: QrCode, inline: false },
  { key: 'weekly', label: 'Weekly Report', icon: Trophy, inline: false },
  { key: 'reports', label: 'All Reports', icon: FileText, inline: false },
  { key: 'uber-trash', label: 'Uber Trash', icon: Zap, inline: false },
  { key: 'specialty-services', label: 'Specialty Services', icon: Package, inline: true },
  { key: 'routes-regions', label: 'Routes & Regions', icon: MapPin, inline: false },
  // Master Addendum · CS Leads CRM lives exclusively in Customer Services.
  { key: 'leads-crm', label: 'CS Leads CRM', icon: FileText, inline: true },
  { key: 'live-tracking', label: 'Live Tracking', icon: Activity, inline: false },
  { key: 'demo-tools', label: 'Demo Tools', icon: Sparkles, inline: false },
];

export function CustomerServiceCommandCenter({ onOpenSection, boardFilter, initialSection, chatSessionId, autoClaim }) {
  const terms = useAdminScopeTerms();
  const [tickets, setTickets] = useState({ open: 0, total: 0 });
  const [violations, setViolations] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Set of tab keys that currently have routed tasks waiting on CS.
  // Coordinates the Inbox with the Full Tab Access strip so the pill
  // that matches an inbound task auto-lights with a red badge (CEO
  // Feb 2026 potential-improvement directive).
  const [tabsWithTasks, setTabsWithTasks] = useState({});
  // Default to Resident Activations — CS's #1 morning triage after the Feb 2026 delegation.
  const [csSection, setCsSection] = useState(initialSection === 'support-chat' ? 'support-chat' : 'resident-activations');
  useEffect(() => { if (initialSection === 'support-chat') setCsSection('support-chat'); }, [initialSection]);
  const chooseCsTab = useDepartmentMenu('cs-operations', 'Service operations', CS_OPS_TABS.map(t => ({...t, label: terms.text(t.label)})), csSection,
    key => handleTabClick(CS_OPS_TABS.find(t => t.key === key)), 'cs-ops-tabs');

  // Source-module hints emitted by the routing engine → tab-key that
  // handles that stream. Keep tight so an unmatched source doesn't
  // spuriously light up random tabs.
  const SOURCE_TO_TAB = {
    violation: 'violations',
    violations: 'violations',
    fleet_safety: 'fleet-safety',
    'fleet-safety': 'fleet-safety',
    safety_incident: 'fleet-safety',
    daily_report: 'daily-reports',
    'daily-reports': 'daily-reports',
    training: 'ops-training-completions',
    training_completion: 'ops-training-completions',
    specialty_service: 'specialty-services',
    'specialty-services': 'specialty-services',
    qr_scan: 'qr-codes',
    'qr-codes': 'qr-codes',
    live_chat: 'support-chat',
    chat: 'support-chat',
    uber_trash: 'uber-trash',
    swap: 'swaps',
    route: 'routes-regions',
    'routes-regions': 'routes-regions',
    live_tracking: 'live-tracking',
    'live-tracking': 'live-tracking',
    weekly_report: 'weekly',
    staffing: 'ops-staffing',
  };

  // Iter-111 · Fix 2 — Cleaners/Valet exclusive data. When view_as is
  // cleaners this board fetches ONLY /api/cleaners/* endpoints.
  const viewAs = boardFilter?.view_as || 'valet';
  const [cleanersSnap, setCleanersSnap] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const qs = boardFilter?.queryString || '';
        // Cleaners data — fetched for 'cleaners' (exclusive) and
        // 'corporate' (aggregated across both lines).
        if (viewAs === 'cleaners' || viewAs === 'corporate') {
          const slRes = await fetch(`${API}/api/cleaners/service-lines?${qs}`, { credentials: 'include' });
          if (slRes.ok) {
            const d = await slRes.json();
            const lines = d.service_lines || [];
            setCleanersSnap({
              properties: lines.reduce((a, l) => a + (l.property_count || 0), 0),
              activeRoutes: lines.reduce((a, l) => a + (l.active_routes || 0), 0),
              violations: lines.reduce((a, l) => a + (l.weekly_violations || 0), 0),
              linesActive: lines.filter((l) => (l.property_count || 0) > 0).length,
            });
          }
        } else {
          setCleanersSnap(null);
        }
        if (viewAs === 'cleaners') {
          // Cleaners-exclusive: only dept-tasks beyond the cleaners feed.
          const dRes = await fetch(`${API}/api/dept-tasks?target_department=customer-service`, { credentials: 'include' });
          if (dRes.ok) {
            const tasks = await dRes.json();
            const counts = {};
            (tasks || []).forEach((task) => {
              if (task.status === 'resolved' || task.status === 'closed') return;
              const key = SOURCE_TO_TAB[String(task.source_module || '').toLowerCase()];
              if (key) counts[key] = (counts[key] || 0) + 1;
            });
            setTabsWithTasks(counts);
          }
          // Shared tickets, reports and staffing below also belong to Cleaners.
        }
        // Valet data ('valet' exclusive, or the valet half of 'corporate').
        // Iter-112 · Item 2 — city/state propagate as query params.
        // Iter-141 · Section 2 — "Open service tickets" now reads TKT-
        // tickets (status open + assigned) instead of /api/service-requests.
        const [tRes, tktRes, vRes, eRes, dRes] = await Promise.all([
          fetch(`${API}/api/service-requests?${qs}`, { credentials: 'include' }),
          fetch(`${API}/api/tickets`, { credentials: 'include' }),
          fetch(`${API}/api/violations?${qs}`, { credentials: 'include' }),
          fetch(`${API}/api/company/employees?${qs}`, { credentials: 'include' }),
          fetch(`${API}/api/dept-tasks?target_department=customer-service`, { credentials: 'include' }),
        ]);
        // Total count remains from legacy service-requests. The Open
        // count is now sourced from TKT- so the tile matches the CS
        // Tickets Board authoritative feed.
        let totalCount = 0;
        if (tRes.ok) {
          const d = await tRes.json();
          totalCount = (d || []).length;
        }
        let openCountTkt = 0;
        if (tktRes.ok) {
          const j = await tktRes.json();
          openCountTkt = (j.tickets || []).filter((t) => ['open', 'assigned'].includes(t.status)).length;
        }
        setTickets({ open: openCountTkt, total: totalCount });
        if (vRes.ok) setViolations(await vRes.json());
        if (eRes.ok) {
          const s = await eRes.json();
          let list = Array.isArray(s) ? s : (s.staff || s.employees || []);
          const scopeCities = boardFilter?.cities || [];
          if (scopeCities.length) list = list.filter((u) => !u.city || scopeCities.includes(u.city));
          setEmployees(list);
        }
        if (dRes.ok) {
          const tasks = await dRes.json();
          const counts = {};
          (tasks || []).forEach((task) => {
            if (task.status === 'resolved' || task.status === 'closed') return;
            const key = SOURCE_TO_TAB[String(task.source_module || '').toLowerCase()];
            if (key) counts[key] = (counts[key] || 0) + 1;
          });
          setTabsWithTasks(counts);
        }
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAs, boardFilter?.queryString, boardFilter?.refreshTick]);

  const openViolations = violations.filter(
    (v) => !['resolved', 'rejected', 'closed'].includes((v.status || '').toLowerCase()),
  ).length;

  const handleTabClick = (tab) => {
    if (tab.inline) {
      setCsSection(tab.key);
    } else if (onOpenSection) {
      // Deep-link into Operations at the requested tab. The parent
      // dashboard already stashes `parentDept='customer-service'` so
      // the user can click the back-breadcrumb to return here.
      onOpenSection(tab.key);
    }
  };

  return (
    <DeptCommandShell
      boardFilter={boardFilter}
      deptKey="customer-service" accent="#06b6d4" icon={Headphones}
      title="CUSTOMER SERVICE HEADQUARTERS"
      onOpenSection={onOpenSection}
      kpis={(() => {
        const clnTiles = [
          { icon: <LayoutDashboard className="h-5 w-5" />, label: 'Cleaners properties', value: cleanersSnap?.properties ?? 0, sub: 'Commercial accounts in scope', testkey: 'cln-properties' },
          { icon: <Activity className="h-5 w-5" />, label: 'Active routes · 24h', value: cleanersSnap?.activeRoutes ?? 0, sub: 'Cleaners crews in motion', testkey: 'cln-routes' },
          { icon: <AlertTriangle className="h-5 w-5" />, label: 'Violations · window', value: cleanersSnap?.violations ?? 0, sub: 'Cleaners lines only', testkey: 'cln-violations' },
          { icon: <Sparkles className="h-5 w-5" />, label: 'Service lines active', value: cleanersSnap?.linesActive ?? 0, sub: 'Of 12 commercial types', testkey: 'cln-lines' },
        ];
        const valetTiles = [
          { icon: <Headphones className="h-5 w-5" />, label: 'Open service tickets', value: tickets.open, sub: 'TKT · Open + Assigned', testkey: 'open' },
          { icon: <FileCheck className="h-5 w-5" />, label: 'Tickets · total', value: tickets.total, sub: 'All-time', testkey: 'total' },
          { icon: <AlertTriangle className="h-5 w-5" />, label: 'Open violations', value: openViolations, sub: `${violations.length} total`, testkey: 'violations' },
          { icon: <Star className="h-5 w-5" />, label: 'Field agents', value: employees.length, sub: 'Read-only roster', testkey: 'agents' },
        ];
        if (viewAs === 'cleaners') return clnTiles;
        if (viewAs === 'corporate') return [...valetTiles, ...clnTiles];
        return valetTiles;
      })()}
    >
      {/* Iter-112 · Item 4 — Prospect Demo Sharing panel (CS-owned). */}
      <CsDemoSharePanel />

      {/* D4/E11 — Service Packages + New Quote Submissions (synchronized with Sales). */}
      <AdminScopeSection exclude="cleaners"><ServicePackagesPanel dept="cs" /></AdminScopeSection>

      {/* ── "Command Center · Access Areas" title bar — moved down here
          (per CEO Feb 2026) to sit between the Inbox and the
          Operations Full Tab Access strip. The old top-of-page card
          row was retired: Demo Tools + Fleet Safety are now surfaced
          as tab pills below, avoiding duplicate entry points. */}
      <section data-testid="customer-service-access-areas-divider">
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-cyan-500/40" />
          <h2 className="text-xs uppercase tracking-[0.3em] font-semibold whitespace-nowrap text-cyan-400">
            Command Center · Access Areas
          </h2>
          <div className="h-px flex-1 bg-cyan-500/40" />
        </div>
      </section>

      {/* Iter-105 · Item 7 — Demo → Live one-click conversion card. */}
      <ConvertibleDemosCard />

      {/* ── Operations tab strip — replicated inside CS HQ so reps
          handle operation management from the same cockpit. Inline
          tabs (Violations · Staffing · Specialty) render below; the
          other 13 deep-link to Operations with a "back to CS"
          breadcrumb already wired in the parent shell. */}
      <Card
        className="bg-zinc-900 border-2 border-cyan-500/40 overflow-hidden"
        data-testid="cs-ops-tabs-card"
      >
        <div className="p-3 border-b border-zinc-800 bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[9px] uppercase tracking-widest text-cyan-400 font-bold">
              Operations · Full Tab Access
            </span>
            <span className="text-[10px] text-zinc-500">
              · <span className="text-[#C9A962]">Inline</span> = handled in CS · <span className="text-cyan-400">↗</span> = opens Ops tab
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="cs-ops-tab-strip">
            {CS_OPS_TABS.map((tab) => {
              const active = tab.inline && csSection === tab.key;
              const SIcon = tab.icon;
              const badgeCount = tab.key === 'violations' ? openViolations
                : tab.key === 'ops-staffing' ? employees.length
                : null;
              const taskAlert = tabsWithTasks[tab.key] || 0;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => chooseCsTab(tab.key)}
                  className={`relative px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all border inline-flex items-center gap-1.5 ${
                    active
                      ? 'bg-cyan-500 text-black border-cyan-500 shadow-[0_0_0_1px_#06b6d4]'
                      : tab.inline
                        ? 'bg-zinc-950 text-[#C9A962] border-[#C9A962]/50 hover:bg-[#C9A962]/10'
                        : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-cyan-500 hover:text-cyan-400'
                  } ${taskAlert > 0 ? 'ring-1 ring-red-500/70 shadow-[0_0_0_1px_rgba(239,68,68,0.35)]' : ''}`}
                  data-testid={`cs-ops-tab-${tab.key}`}
                  title={
                    taskAlert > 0
                      ? `${tab.label} — ${taskAlert} routed task${taskAlert === 1 ? '' : 's'} waiting`
                      : tab.inline ? `${tab.label} — handled inside CS HQ` : `${tab.label} — opens the Operations tab`
                  }
                >
                  <SIcon className="h-3 w-3" />
                  {terms.text(tab.label)}
                  {badgeCount != null && (
                    <span className={`ml-0.5 rounded px-1.5 py-0.5 text-[10px] font-mono ${active ? 'bg-black/30 text-black' : 'bg-zinc-800 text-cyan-400'}`}>
                      {badgeCount}
                    </span>
                  )}
                  {!tab.inline && (
                    <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                  )}
                  {taskAlert > 0 && (
                    <span
                      className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-mono font-bold border border-zinc-950 animate-pulse flex items-center justify-center"
                      data-testid={`cs-ops-tab-${tab.key}-task-alert`}
                    >
                      {taskAlert}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-4">
          {csSection === 'resident-activations' && (
            <div data-testid="cs-resident-activations-section">
              <ResidentApprovalsQueue />
            </div>
          )}
          {csSection === 'support-chat' && (
            <div data-testid="cs-live-chats-section">
              <AdminSupportChat initialSessionId={chatSessionId} autoClaim={autoClaim} />
            </div>
          )}
          {csSection === 'rewards' && <div className="flex flex-wrap gap-4 py-5"><a href="/rewards-review" className="text-[#C9A962] underline" data-testid="cs-staff-reward-review-link">Staff payroll reward review</a><a href="/resident-rewards-admin" className="text-[#C9A962] underline" data-testid="cs-resident-rewards-link">Resident contracts, deductions & bag deliveries</a></div>}
          {csSection === 'violations' && (
            <div data-testid="cs-violations-section">
              <AdminViolationReportsTab allViolations={violations} />
            </div>
          )}
          {csSection === 'ops-staffing' && (
            <div data-testid="cs-staffing-section">
              <OpsStaffingRoster employees={employees} />
            </div>
          )}
          {csSection === 'specialty-services' && (
            <div data-testid="cs-specialty-section">
              <AdminSpecialtyServices />
            </div>
          )}
          {csSection === 'leads-crm' && (
            <div data-testid="cs-leads-crm-section">
              <LeadsInboundCRMTab />
            </div>
          )}
        </div>
      </Card>
      {/* S1/S7 — Resident App community codes + announcements (CS owns resident lifecycle). */}
      <CommunityCodesPanel />
      {/* S6/S7 Phase 3 — resident accounts, violation review, delivery log. */}
      <ResidentOpsPanel />
      {/* Section 5 (iter-130) · Auto-quote for existing clients. */}
      <AutoQuoteExistingClientPanel testId="cs-auto-quote-panel" />
      {/* Section 7 (iter-134) · Contract editor. */}
      <CSContractEditorPanel testId="cs-contract-editor-panel" />
      {/* Section 3 (iter-140) · Customer Service Tickets Board (TKT-). */}
      <CSTicketsBoardPanel testId="cs-tickets-board-panel" />
      {/* Iter-194 · Trashcan Fulfillment Board — CS + Ops queue with
          one-tap "Delegate to Ops · Same Day" action. */}
      <TrashcanFulfillmentBoard testIdPrefix="cs-trashcan-board" showComplete />
      {/* Section 9 (iter-137) · New Account Onboarding — compact list
          view. Relocated here from Operations CC (Section 8). */}
      <CSOnboardingListPanel testId="cs-onboarding-list-panel" />
      {/* Iter-150 · Red Team Fix 3 — public demo entry for CS reps. */}
      <ExplorePlatformTile testId="cs-explore-platform" />
      {/* Iter-156 · Phase 3 — CS tab-and-board with Quote Engine +
          Invoices + Renewals so reps can quote and reissue billing
          without leaving the command center. */}
      <DeptTabBoard
        testidPrefix="cs-tab-board"
        accent="#22d3ee"
        tabs={[
          ...PHASE3_PROPERTY_TABS,
          { key: 'quote-engine', label: 'Quote Engine', icon: Sparkles, render: () => <QuoteEngineBoard /> },
          { key: 'renewals',     label: 'Renewal Alerts', icon: Package, render: () => <RenewalAlertsBoard /> },
        ]}
      />
      {/* Routes & Regions is provided by DeptCommandShell (auto-expanded
          for Customer Services as a full-access department). */}
    </DeptCommandShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Procurement & Supply Chain                                               */
/* ═══════════════════════════════════════════════════════════════════════ */
export function ProcurementCommandCenter({ onOpenSection, boardFilter }) {
  const [brokers, setBrokers] = useState([]);
  const [fleet, setFleet] = useState({ vehicles: 0, incidents30: 0, openIncidents: 0, safetyChecks: 0 });
  useEffect(() => {
    (async () => {
      try {
        const qs = boardFilter?.queryString ? `?${boardFilter.queryString}` : '';
        const [bRes, vRes, iRes, sRes] = await Promise.all([
          fetch(`${API}/api/brokers${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/api/procurement/vehicles${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/api/safety-incidents${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/api/vehicle-safety${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
        ]);
        setBrokers(Array.isArray(bRes) ? bRes : []);
        const incidentsArr = Array.isArray(iRes) ? iRes : [];
        const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
        setFleet({
          vehicles: (vRes || []).length,
          incidents30: incidentsArr.filter((i) => {
            const t = new Date(i.created_at || 0).getTime();
            return t && t > cutoff;
          }).length,
          openIncidents: incidentsArr.filter((i) => (i.status || 'open') === 'open').length,
          safetyChecks: (Array.isArray(sRes) ? sRes : []).length,
        });
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.queryString, boardFilter?.refreshTick]);
  const spend = brokers.reduce((acc, b) => acc + (Number(b.total_spend) || 0), 0);
  return (
    <DeptCommandShell
      boardFilter={boardFilter}
      deptKey="procurement" accent="#a855f7" icon={Truck}
      title="PROCUREMENT HEADQUARTERS"
      onOpenSection={onOpenSection}
      kpis={[
        { icon: <Truck className="h-5 w-5" />, label: 'Active suppliers', value: brokers.length, sub: 'On the ledger', testkey: 'suppliers' },
        { icon: <DollarSign className="h-5 w-5" />, label: 'Historical spend', value: `$${spend.toLocaleString()}`, sub: 'Legacy purchases · all dates', testkey: 'spend' },
        { icon: <Package className="h-5 w-5" />, label: 'PO backlog', value: '—', sub: 'Wire from ERP', testkey: 'po' },
        { icon: <ShieldCheck className="h-5 w-5" />, label: 'EPA-compliant sourcing', value: '—', sub: 'Sustainability index', testkey: 'epa' },
        // Fleet Safety row — per CEO directive procurement co-owns fleet
        // safety data with the Safety & Risk department.
        { icon: <Truck className="h-5 w-5" />, label: 'Fleet vehicles', value: fleet.vehicles, sub: 'Under procurement oversight', testkey: 'fleet-vehicles' },
        { icon: <Siren className="h-5 w-5" />, label: 'Open safety incidents', value: fleet.openIncidents, sub: 'Awaiting review', testkey: 'fleet-open-incidents' },
        { icon: <AlertTriangle className="h-5 w-5" />, label: 'Incidents · 30 days', value: fleet.incidents30, sub: 'Rolling window', testkey: 'fleet-incidents-30' },
        { icon: <ShieldCheck className="h-5 w-5" />, label: 'Safety checks filed', value: fleet.safetyChecks, sub: 'Pre-/post-shift', testkey: 'fleet-safety-checks' },
      ]}
    >
      <ProcurementVehiclesPanel />
      <nav className="flex flex-wrap gap-4" data-testid="procurement-workspace-links"><a href="/warehouse" className="inline-flex items-center gap-2 text-[#C9A962] underline font-semibold" data-testid="procurement-warehouse-link"><Package className="h-4 w-4"/>Warehouse · Stock, deliveries & audits</a><a href="/suppliers" className="inline-flex items-center gap-2 text-[#C9A962] underline font-semibold" data-testid="procurement-suppliers-link"><Truck className="h-4 w-4"/>Brokers & Providers · Prices & purchases</a></nav>
      <ProcurementGasCardsPanel />
      <VehicleMpgChart testId="procurement-mpg-chart" />
      {/* Iter-194 · Same Trashcan Fulfillment queue mirrored here so
          Ops dispatch grabs the same-day handoff without leaving
          Procurement HQ. */}
      <TrashcanFulfillmentBoard testIdPrefix="ops-trashcan-board" showComplete />
    </DeptCommandShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Safety & Risk Management                                                 */
/* ═══════════════════════════════════════════════════════════════════════ */
export function SafetyCommandCenter({ onOpenSection, boardFilter }) {
  const [safety, setSafety] = useState({ vehicles: 0, incidents30: 0, safetyChecks: 0 });
  // Iter-111 · Fix 2 — exclusive Cleaners data path.
  const viewAs = boardFilter?.view_as || 'valet';
  const [cleanersSnap, setCleanersSnap] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const qs = boardFilter?.queryString || '';
        if (viewAs === 'cleaners' || viewAs === 'corporate') {
          const r = await fetch(`${API}/api/cleaners/service-lines?${qs}`, { credentials: 'include' });
          if (r.ok) {
            const d = await r.json();
            const lines = d.service_lines || [];
            setCleanersSnap({
              properties: lines.reduce((a, l) => a + (l.property_count || 0), 0),
              activeRoutes: lines.reduce((a, l) => a + (l.active_routes || 0), 0),
              violations: lines.reduce((a, l) => a + (l.weekly_violations || 0), 0),
              linesActive: lines.filter((l) => (l.property_count || 0) > 0).length,
            });
          }
          if (viewAs === 'cleaners') return;
        } else {
          setCleanersSnap(null);
        }
        const [vRes, violRes, sfRes] = await Promise.all([
          fetch(`${API}/api/procurement/vehicles?${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/api/violations?${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/api/vehicle-safety?${qs}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
        ]);
        // Incidents = violations of type safety_hazard/fire_hazard/blocked_exit in last 30 days
        const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
        const incidents30 = (Array.isArray(violRes) ? violRes : []).filter((v) => {
          const t = new Date(v.created_at).getTime();
          const type = (v.violation_type || '').toLowerCase();
          return t > cutoff && ['safety_hazard', 'fire_hazard', 'blocked_exit', 'accident', 'injury'].some((k) => type.includes(k));
        }).length;
        setSafety({
          vehicles: (vRes || []).length,
          incidents30,
          safetyChecks: (Array.isArray(sfRes) ? sfRes : []).length,
        });
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAs, boardFilter?.queryString, boardFilter?.refreshTick]);
  return (
    <DeptCommandShell
      boardFilter={boardFilter}
      deptKey="safety" accent="#ef4444" icon={ShieldCheck}
      title="SAFETY & RISK HEADQUARTERS"
      onOpenSection={onOpenSection}
      kpis={(() => {
        const clnTiles = [
          { icon: <LayoutDashboard className="h-5 w-5" />, label: 'Cleaners properties', value: cleanersSnap?.properties ?? 0, sub: 'Commercial accounts in scope', testkey: 'cln-properties' },
          { icon: <Siren className="h-5 w-5" />, label: 'Violations · window', value: cleanersSnap?.violations ?? 0, sub: 'Cleaners lines only', testkey: 'cln-violations' },
          { icon: <Activity className="h-5 w-5" />, label: 'Active routes · 24h', value: cleanersSnap?.activeRoutes ?? 0, sub: 'Cleaners crews in motion', testkey: 'cln-routes' },
          { icon: <Sparkles className="h-5 w-5" />, label: 'Service lines active', value: cleanersSnap?.linesActive ?? 0, sub: 'Of 12 commercial types', testkey: 'cln-lines' },
        ];
        const valetTiles = [
          { icon: <Truck className="h-5 w-5" />, label: 'Vehicle fleet', value: safety.vehicles, sub: 'Under safety oversight', testkey: 'vehicles' },
          { icon: <Siren className="h-5 w-5" />, label: 'Incidents · 30 days', value: safety.incidents30, sub: 'Safety hazards + injuries', testkey: 'incidents' },
          { icon: <ShieldCheck className="h-5 w-5" />, label: 'Safety checks logged', value: safety.safetyChecks, sub: 'Pre-/post-shift', testkey: 'checks' },
          { icon: <FileCheck className="h-5 w-5" />, label: 'OSHA certifications', value: '—', sub: 'Wire from training', testkey: 'osha' },
        ];
        if (viewAs === 'cleaners') return clnTiles;
        if (viewAs === 'corporate') return [...valetTiles, ...clnTiles];
        return valetTiles;
      })()}
    >
      <div className="flex flex-wrap gap-4 py-4"><a href="/necs-standard-review" className="text-[#C9A962] underline" data-testid="safety-necs-standard-link">NECS Standard shift reports</a><a href="/closeout-attention" className="text-amber-300 underline" data-testid="safety-attention-link">Overdue & returned work</a></div>
      {viewAs !== 'cleaners' && (
      <GeoFilterProvider>
        <div className="mt-6 space-y-6" data-testid="safety-dept-scorecard">
          <GeoFilterBar />
          <SafetyRiskPanel />
        </div>
      </GeoFilterProvider>
      )}
    </DeptCommandShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Training & Development                                                   */
/* ═══════════════════════════════════════════════════════════════════════ */
export function TrainingCommandCenter({ onOpenSection, boardFilter }) {
  const [training, setTraining] = useState({ courses: 0 });
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/training/courses`, { credentials: 'include' });
        if (r.ok) { const d = await r.json(); setTraining({ courses: d.length || 0 }); }
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.queryString, boardFilter?.refreshTick]);
  return (
    <DeptCommandShell
      boardFilter={boardFilter}
      deptKey="training" accent="#0ea5e9" icon={GraduationCap}
      title="TRAINING HEADQUARTERS"
      onOpenSection={onOpenSection}
      kpis={[
        { icon: <BookOpen className="h-5 w-5" />, label: 'Active courses', value: training.courses, sub: 'Curriculum library', testkey: 'courses' },
        { icon: <GraduationCap className="h-5 w-5" />, label: 'Completions · 30 days', value: '—', sub: 'Certificates issued', testkey: 'completions' },
        { icon: <TrendingUp className="h-5 w-5" />, label: 'Skill-up rate', value: '—', sub: 'Enrollment index', testkey: 'skillup' },
        { icon: <FileCheck className="h-5 w-5" />, label: 'Re-certs due', value: '—', sub: 'Next 30 days', testkey: 'recerts' },
      ]}
    ><a href="/necs-standard-review" className="inline-flex text-[#C9A962] underline py-4" data-testid="training-necs-standard-link">NECS Standard briefings & completed shift reports</a></DeptCommandShell>
  );
}
